import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconBuilding,
  IconCheck,
  IconCopy,
  IconEdit,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api } from '../api';
import { useAuthStore } from '../stores/auth-store';
import { apiHook } from '../treaty';
import { apiErrorMessage } from '../utils/api-error';

type ValueType = 'text' | 'multiline';

async function writeToClipboard(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.append(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  textarea.remove();
  if (!copied) throw new Error('Clipboard access is unavailable');
}

function numericOrder(value: string | number) {
  return typeof value === 'number' && Number.isInteger(value) ? value : 0;
}

export function BusinessInformationPage() {
  const isAdmin = useAuthStore((state) => state.user?.role === 'admin');
  const queryClient = useQueryClient();
  const informationQuery = apiHook.useQuery((client) => client['business-information'].get());
  const sections = informationQuery.data ?? [];
  const [copiedFieldId, setCopiedFieldId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [sectionModalOpen, setSectionModalOpen] = useState(false);
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [sectionName, setSectionName] = useState('');
  const [sectionOrder, setSectionOrder] = useState<string | number>(0);

  const [fieldModalOpen, setFieldModalOpen] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [fieldSectionId, setFieldSectionId] = useState<string | null>(null);
  const [fieldLabel, setFieldLabel] = useState('');
  const [fieldValue, setFieldValue] = useState('');
  const [fieldValueType, setFieldValueType] = useState<ValueType>('text');
  const [fieldOrder, setFieldOrder] = useState<string | number>(0);

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ['business-information'] });
  }

  function openNewSection() {
    setEditingSectionId(null);
    setSectionName('');
    setSectionOrder(
      sections.length ? Math.max(...sections.map((section) => section.sortOrder)) + 10 : 0,
    );
    setSectionModalOpen(true);
  }

  function openEditSection(section: (typeof sections)[number]) {
    setEditingSectionId(section.id);
    setSectionName(section.name);
    setSectionOrder(section.sortOrder);
    setSectionModalOpen(true);
  }

  function openNewField(sectionId: string) {
    const section = sections.find((candidate) => candidate.id === sectionId);
    setEditingFieldId(null);
    setFieldSectionId(sectionId);
    setFieldLabel('');
    setFieldValue('');
    setFieldValueType('text');
    setFieldOrder(
      section?.fields.length ? Math.max(...section.fields.map((field) => field.sortOrder)) + 10 : 0,
    );
    setFieldModalOpen(true);
  }

  function openEditField(sectionId: string, field: (typeof sections)[number]['fields'][number]) {
    setEditingFieldId(field.id);
    setFieldSectionId(sectionId);
    setFieldLabel(field.label);
    setFieldValue(field.value);
    setFieldValueType(field.valueType);
    setFieldOrder(field.sortOrder);
    setFieldModalOpen(true);
  }

  async function saveSection(event: React.FormEvent) {
    event.preventDefault();
    if (!sectionName.trim()) return;
    setSaving(true);
    const body = { name: sectionName, sortOrder: numericOrder(sectionOrder) };
    const response = editingSectionId
      ? await api.api.api.v1.admin['business-information']
          .sections({ id: editingSectionId })
          .put(body)
      : await api.api.api.v1.admin['business-information'].sections.post(body);
    setSaving(false);
    if (response.error) {
      notifications.show({
        color: 'red',
        message: apiErrorMessage(response.error, {
          fallback: 'The section could not be saved.',
          status: response.status,
        }),
      });
      return;
    }
    setSectionModalOpen(false);
    await refresh();
    notifications.show({ color: 'green', message: 'Section saved.' });
  }

  async function saveField(event: React.FormEvent) {
    event.preventDefault();
    if (!fieldSectionId || !fieldLabel.trim() || !fieldValue.trim()) return;
    setSaving(true);
    const body = {
      sectionId: fieldSectionId,
      label: fieldLabel,
      value: fieldValue,
      valueType: fieldValueType,
      sortOrder: numericOrder(fieldOrder),
    };
    const response = editingFieldId
      ? await api.api.api.v1.admin['business-information'].fields({ id: editingFieldId }).put(body)
      : await api.api.api.v1.admin['business-information'].fields.post(body);
    setSaving(false);
    if (response.error) {
      notifications.show({
        color: 'red',
        message: apiErrorMessage(response.error, {
          fallback: 'The field could not be saved.',
          status: response.status,
        }),
      });
      return;
    }
    setFieldModalOpen(false);
    await refresh();
    notifications.show({ color: 'green', message: 'Business information saved.' });
  }

  function confirmDeleteSection(section: (typeof sections)[number]) {
    modals.openConfirmModal({
      title: 'Delete section?',
      children: (
        <Text size='sm'>
          Delete “{section.name}” and all {section.fields.length} fields inside it? This cannot be
          undone.
        </Text>
      ),
      labels: { confirm: 'Delete section', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        const response = await api.api.api.v1.admin['business-information']
          .sections({
            id: section.id,
          })
          .delete();
        if (response.error) {
          notifications.show({ color: 'red', message: 'The section could not be deleted.' });
          return;
        }
        await refresh();
        notifications.show({ color: 'green', message: 'Section deleted.' });
      },
    });
  }

  function confirmDeleteField(field: (typeof sections)[number]['fields'][number]) {
    modals.openConfirmModal({
      title: 'Delete field?',
      children: <Text size='sm'>Delete “{field.label}”? This cannot be undone.</Text>,
      labels: { confirm: 'Delete field', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        const response = await api.api.api.v1.admin['business-information']
          .fields({
            id: field.id,
          })
          .delete();
        if (response.error) {
          notifications.show({ color: 'red', message: 'The field could not be deleted.' });
          return;
        }
        await refresh();
        notifications.show({ color: 'green', message: 'Field deleted.' });
      },
    });
  }

  async function copyField(id: string, label: string, value: string) {
    try {
      await writeToClipboard(value);
      setCopiedFieldId(id);
      window.setTimeout(
        () => setCopiedFieldId((current) => (current === id ? null : current)),
        1800,
      );
      notifications.show({ color: 'green', message: `${label} copied.` });
    } catch {
      notifications.show({ color: 'red', message: 'Clipboard access is unavailable.' });
    }
  }

  return (
    <Stack gap='lg' maw={1100} mx='auto'>
      <Group justify='space-between' align='end'>
        <div>
          <Title order={1}>Business information</Title>
          <Text c='dimmed' mt={5}>
            Find and copy the company details used in forms, invoices, and registrations.
          </Text>
        </div>
        {isAdmin && (
          <Button leftSection={<IconPlus size={18} />} onClick={openNewSection}>
            Add section
          </Button>
        )}
      </Group>

      {isAdmin && (
        <Alert color='blue' icon={<IconAlertCircle size={18} />}>
          Everyone with a Disit Internal account can see these values. Do not add passwords, private
          keys, or account credentials.
        </Alert>
      )}

      {informationQuery.isLoading && (
        <Center py='xl'>
          <Loader />
        </Center>
      )}
      {informationQuery.isError && (
        <Alert color='red' icon={<IconAlertCircle size={18} />}>
          {informationQuery.error.message}
        </Alert>
      )}
      {!informationQuery.isLoading && !informationQuery.isError && sections.length === 0 && (
        <Paper withBorder radius='lg' p='xl'>
          <Stack align='center' gap='sm' py='xl'>
            <IconBuilding size={38} color='var(--mantine-color-gray-5)' />
            <Text fw={600}>No business information yet</Text>
            <Text c='dimmed' ta='center' maw={500}>
              {isAdmin
                ? 'Create a section such as Company details, Tax information, or Addresses, then add fields to it.'
                : 'An administrator has not added any shared business details yet.'}
            </Text>
            {isAdmin && (
              <Button variant='light' leftSection={<IconPlus size={18} />} onClick={openNewSection}>
                Create first section
              </Button>
            )}
          </Stack>
        </Paper>
      )}

      {sections.map((section) => (
        <Paper
          key={section.id}
          withBorder
          radius='lg'
          p={{ base: 'md', sm: 'xl' }}
          className='data-card'
        >
          <Group justify='space-between' align='center' mb='lg'>
            <Group gap='sm'>
              <Title order={2} size='h3'>
                {section.name}
              </Title>
              <Badge variant='light' color='gray'>
                {section.fields.length} {section.fields.length === 1 ? 'field' : 'fields'}
              </Badge>
            </Group>
            {isAdmin && (
              <Group gap={6}>
                <Button
                  variant='light'
                  size='xs'
                  leftSection={<IconPlus size={15} />}
                  onClick={() => openNewField(section.id)}
                >
                  Add field
                </Button>
                <Tooltip label='Edit section'>
                  <ActionIcon
                    variant='subtle'
                    aria-label={`Edit ${section.name}`}
                    onClick={() => openEditSection(section)}
                  >
                    <IconEdit size={17} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label='Delete section'>
                  <ActionIcon
                    variant='subtle'
                    color='red'
                    aria-label={`Delete ${section.name}`}
                    onClick={() => confirmDeleteSection(section)}
                  >
                    <IconTrash size={17} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            )}
          </Group>

          {section.fields.length === 0 ? (
            <Box py='md'>
              <Text c='dimmed'>No fields in this section.</Text>
            </Box>
          ) : (
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing='md'>
              {section.fields.map((field) => (
                <Paper
                  key={field.id}
                  withBorder
                  radius='md'
                  p='md'
                  bg='var(--mantine-color-gray-0)'
                >
                  <Group justify='space-between' align='flex-start' wrap='nowrap'>
                    <Box miw={0}>
                      <Text size='xs' fw={700} tt='uppercase' c='dimmed' lts={0.4}>
                        {field.label}
                      </Text>
                      <Text
                        mt={7}
                        fw={500}
                        style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                      >
                        {field.value}
                      </Text>
                    </Box>
                    <Group gap={4} wrap='nowrap'>
                      <Tooltip
                        label={copiedFieldId === field.id ? 'Copied' : `Copy ${field.label}`}
                      >
                        <ActionIcon
                          variant={copiedFieldId === field.id ? 'filled' : 'light'}
                          color={copiedFieldId === field.id ? 'green' : 'blue'}
                          aria-label={`Copy ${field.label}`}
                          onClick={() => void copyField(field.id, field.label, field.value)}
                        >
                          {copiedFieldId === field.id ? (
                            <IconCheck size={17} />
                          ) : (
                            <IconCopy size={17} />
                          )}
                        </ActionIcon>
                      </Tooltip>
                      {isAdmin && (
                        <>
                          <Tooltip label='Edit field'>
                            <ActionIcon
                              variant='subtle'
                              aria-label={`Edit ${field.label}`}
                              onClick={() => openEditField(section.id, field)}
                            >
                              <IconEdit size={17} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label='Delete field'>
                            <ActionIcon
                              variant='subtle'
                              color='red'
                              aria-label={`Delete ${field.label}`}
                              onClick={() => confirmDeleteField(field)}
                            >
                              <IconTrash size={17} />
                            </ActionIcon>
                          </Tooltip>
                        </>
                      )}
                    </Group>
                  </Group>
                </Paper>
              ))}
            </SimpleGrid>
          )}
        </Paper>
      ))}

      <Modal
        opened={sectionModalOpen}
        onClose={() => setSectionModalOpen(false)}
        title={editingSectionId ? 'Edit section' : 'Add section'}
      >
        <form onSubmit={saveSection}>
          <Stack>
            <TextInput
              label='Section name'
              placeholder='Tax information'
              required
              maxLength={100}
              value={sectionName}
              onChange={(event) => setSectionName(event.currentTarget.value)}
            />
            <NumberInput
              label='Display order'
              description='Lower numbers appear first.'
              min={0}
              max={10_000}
              allowDecimal={false}
              value={sectionOrder}
              onChange={setSectionOrder}
            />
            <Group justify='flex-end'>
              <Button variant='default' onClick={() => setSectionModalOpen(false)}>
                Cancel
              </Button>
              <Button type='submit' loading={saving} disabled={!sectionName.trim()}>
                Save section
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={fieldModalOpen}
        onClose={() => setFieldModalOpen(false)}
        title={editingFieldId ? 'Edit field' : 'Add field'}
        size='lg'
      >
        <form onSubmit={saveField}>
          <Stack>
            <Select
              label='Section'
              required
              data={sections.map((section) => ({ value: section.id, label: section.name }))}
              value={fieldSectionId}
              onChange={setFieldSectionId}
            />
            <TextInput
              label='Label'
              placeholder='GST number'
              required
              maxLength={100}
              value={fieldLabel}
              onChange={(event) => setFieldLabel(event.currentTarget.value)}
            />
            <Select
              label='Value format'
              data={[
                { value: 'text', label: 'Single line' },
                { value: 'multiline', label: 'Multiple lines' },
              ]}
              value={fieldValueType}
              onChange={(value) => value && setFieldValueType(value as ValueType)}
            />
            {fieldValueType === 'multiline' ? (
              <Textarea
                label='Value'
                placeholder='Enter the complete address or other multiline value'
                required
                autosize
                minRows={4}
                maxRows={10}
                maxLength={10_000}
                value={fieldValue}
                onChange={(event) => setFieldValue(event.currentTarget.value)}
              />
            ) : (
              <TextInput
                label='Value'
                placeholder='Enter the value users should copy'
                required
                maxLength={10_000}
                value={fieldValue}
                onChange={(event) => setFieldValue(event.currentTarget.value)}
              />
            )}
            <NumberInput
              label='Display order'
              description='Lower numbers appear first.'
              min={0}
              max={10_000}
              allowDecimal={false}
              value={fieldOrder}
              onChange={setFieldOrder}
            />
            <Group justify='flex-end'>
              <Button variant='default' onClick={() => setFieldModalOpen(false)}>
                Cancel
              </Button>
              <Button
                type='submit'
                loading={saving}
                disabled={!fieldSectionId || !fieldLabel.trim() || !fieldValue.trim()}
              >
                Save field
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
